import axios from 'axios';
import { API_CONFIG } from '../config/config';
import { cookieService } from '../services/cookieService';
import { authService } from '../services/authService';

const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
  // Enable sending cookies with cross-origin requests (HttpOnly cookies)
  withCredentials: true,
});

// Remove dead backend refresh client; refresh is handled via Firebase SDK

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor - with HttpOnly cookies, we don't need to manually attach tokens
// The browser automatically sends cookies with withCredentials: true
// We only keep this for backward compatibility during migration
apiClient.interceptors.request.use(
  async (config) => {
    // Si 'skipAuthInterceptor' est défini, ne pas intercepter
    if (config.headers.skipAuthInterceptor) {
      delete config.headers.skipAuthInterceptor; // Nettoyer le header
      return config;
    }

    // Auth tokens are sent automatically via HttpOnly cookies (withCredentials: true)
    // No need to add Authorization header from localStorage
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle Rate Limit (429) - dispatch custom event for toast notification
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'] || 60;
      const detail = error.response.data?.detail || `Trop de requêtes. Réessayez dans ${retryAfter}s.`;
      
      // Dispatch custom event that ToastContext can listen to
      window.dispatchEvent(new CustomEvent('api-rate-limit', { 
        detail: { message: detail, retryAfter } 
      }));
      
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Prevent infinite loop: if the failed request is the refresh endpoint itself, don't retry
      if (originalRequest.url?.includes('/auth/session/refresh')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            // Retry the request - cookies will be sent automatically
            return apiClient(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh using HttpOnly cookie via backend endpoint first
        const refreshResponse = await apiClient.post('/auth/session/refresh', {}, {
          headers: { skipAuthInterceptor: true }
        });
        
        if (refreshResponse.data?.success) {
          // Session refreshed via HttpOnly cookie - just retry the request
          processQueue(null);
          isRefreshing = false;
          return apiClient(originalRequest);
        }
        
        throw new Error('Session refresh failed');
      } catch (refreshError) {
        // Fallback: Try Firebase SDK refresh
        try {
          const newIdToken = await authService.refreshToken();
          
          if (!newIdToken) {
            throw new Error('Unable to refresh access token');
          }
          
          // Update header for this request
          originalRequest.headers.Authorization = `Bearer ${newIdToken}`;
          processQueue(null);
          isRefreshing = false;
          
          return apiClient(originalRequest);
        } catch (err) {
          processQueue(err, null);
          isRefreshing = false;
          cookieService.clearAll();
          // Ensure logout is not called multiple times
          if (!originalRequest._logoutAttempted) {
            originalRequest._logoutAttempted = true;
            await authService.logout();
          }
          return Promise.reject(err);
        }
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
